import React, { useState } from "react";
import { motion } from "framer-motion";

const FoodInsightsGraph: React.FC = () => {
    const [selectedMeal, setSelectedMeal] = useState<number | null>(null);

    const meals = [
        {
            time: "۸:۳۰ صبح",
            name: "صبحانه",
            foods: ["نان و پنیر", "عسل", "گردو"],
            glucose: 95,
            spike: 15,
            recovery: "۴۵ دقیقه",
            rating: "good",
        },
        {
            time: "۱۲:۳۰ ظهر",
            name: "میان‌وعده",
            foods: ["سیب"],
            glucose: 85,
            spike: 8,
            recovery: "۲۰ دقیقه",
            rating: "excellent",
        },
        {
            time: "۱۴:۰۰ بعدازظهر",
            name: "ناهار",
            foods: ["برنج", "مرغ", "سالاد"],
            glucose: 135,
            spike: 45,
            recovery: "۱۲۰ دقیقه",
            rating: "moderate",
        },
        {
            time: "۱۷:۰۰ عصر",
            name: "میان‌وعده",
            foods: ["بیسکویت"],
            glucose: 145,
            spike: 55,
            recovery: "۹۰ دقیقه",
            rating: "poor",
        },
    ];

    const getRatingColor = (rating: string) => {
        switch (rating) {
            case "excellent":
                return "#32D74B";
            case "good":
                return "#66D4CF";
            case "moderate":
                return "#FFD60A";
            case "poor":
                return "#FF3B30";
            default:
                return "#9EA3A9";
        }
    };

    const getRatingText = (rating: string) => {
        switch (rating) {
            case "excellent":
                return "عالی";
            case "good":
                return "خوب";
            case "moderate":
                return "متوسط";
            case "poor":
                return "ضعیف";
            default:
                return "نامشخص";
        }
    };

    return (
        <div className="space-y-3" dir="rtl">
            <div>
                <h4 className="font-medium text-[#002B5C] mb-3">تحلیل وعده‌های غذایی امروز</h4>

                {/* Daily Glucose Curve */}
                <div className="h-32 bg-[#ECEFF1] rounded-[16px] p-3 mb-4 shadow-[0_2px_8px_rgba(0,0,0,0.02)] relative">
                    <svg className="w-full h-full" viewBox="0 0 400 100">
                        {/* Optimal Range */}
                        <rect x="0" y="30" width="400" height="40" fill="#32D74B" opacity="0.1" />
                        <line x1="0" y1="30" x2="400" y2="30" stroke="#32D74B" strokeWidth="1" strokeDasharray="5,5" opacity="0.5" />
                        <line x1="0" y1="70" x2="400" y2="70" stroke="#32D74B" strokeWidth="1" strokeDasharray="5,5" opacity="0.5" />

                        {/* Glucose Curve */}
                        <motion.path
                            d="M 0 60 Q 50 55 100 45 T 150 50 Q 200 30 250 25 T 300 35 Q 350 50 400 55"
                            fill="none"
                            stroke="#0072CE"
                            strokeWidth="3"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{ duration: 2 }}
                        />

                        {/* Meal Points */}
                        {[100, 150, 250, 300].map((x, i) => (
                            <motion.circle
                                key={i}
                                cx={x}
                                cy={[45, 50, 25, 35][i]}
                                r="6"
                                fill={getRatingColor(meals[i].rating)}
                                stroke="white"
                                strokeWidth="2"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: i * 0.2 }}
                                style={{ cursor: "pointer" }}
                                onClick={() => setSelectedMeal(i)}
                            />
                        ))}
                    </svg>

                    <div className="absolute top-2 right-3 text-[10px] text-[#9EA3A9]">
                        <div className="flex items-center gap-1 mb-1">
                            <div className="w-8 h-1 bg-[#32D74B] opacity-50"></div>
                            <span>محدوده مطلوب</span>
                        </div>
                    </div>
                </div>

                {/* Meals List */}
                <div className="space-y-2">
                    {meals.map((meal, i) => (
                        <motion.div
                            key={i}
                            className={`p-3 rounded-[16px] cursor-pointer transition-all ${
                                selectedMeal === i ? "bg-gradient-to-r from-[#0072CE]/10 to-[#00E5FF]/5 border-2 border-[#0072CE]" : "bg-[#ECEFF1] border-2 border-transparent"
                            }`}
                            onClick={() => setSelectedMeal(i)}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <div>
                                    <div className="text-sm font-medium text-[#002B5C]">{meal.name}</div>
                                    <div className="text-xs text-[#9EA3A9]">{meal.time}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-medium" style={{ color: getRatingColor(meal.rating) }}>
                                        {getRatingText(meal.rating)}
                                    </div>
                                    <div className="text-xs text-[#9EA3A9]">جهش: +{meal.spike} mg/dL</div>
                                </div>
                            </div>

                            {selectedMeal === i && (
                                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} transition={{ duration: 0.3 }}>
                                    <div className="border-t border-[#9EA3A9]/20 pt-2 mt-2">
                                        <div className="text-xs text-[#4A5568] mb-2">
                                            <span className="font-medium">محتوا:</span> {meal.foods.join("، ")}
                                        </div>
                                        <div className="flex items-center justify-between text-xs">
                                            <div>
                                                <span className="text-[#9EA3A9]">قند خون:</span> <span className="font-medium text-[#0072CE]">{meal.glucose} mg/dL</span>
                                            </div>
                                            <div>
                                                <span className="text-[#9EA3A9]">زمان بازگشت:</span> <span className="font-medium text-[#0072CE]">{meal.recovery}</span>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </motion.div>
                    ))}
                </div>

                {/* Food Recommendations */}
                <div className="mt-4 p-3 bg-gradient-to-r from-[#32D74B]/10 to-[#66D4CF]/10 rounded-[16px] border border-[#32D74B]/30">
                    <h5 className="text-sm font-medium text-[#32D74B] mb-2 flex items-center gap-2">
                        <span>💡</span>
                        توصیه‌های تغذیه‌ای
                    </h5>
                    <ul className="text-xs text-[#4A5568] space-y-1">
                        <li className="flex items-start gap-2">
                            <span className="text-[#FF3B30] mt-0.5">⚠️</span>
                            <span>
                                <strong>بیسکویت در عصر</strong> باعث جهش ۵۵ واحدی قند شد. جایگزین پیشنهادی: میوه‌های خشک یا آجیل
                            </span>
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="text-[#32D74B] mt-0.5">✓</span>
                            <span>
                                <strong>سیب در میان‌وعده</strong> انتخاب عالی بود، قند خون پایدار ماند.
                            </span>
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="text-[#FFD60A] mt-0.5">⚡</span>
                            <span>در ناهار، افزودن سبزیجات بیشتر می‌تواند جهش قند را کاهش دهد.</span>
                        </li>
                    </ul>
                </div>

                {/* Pattern Analysis */}
                <div className="mt-3 p-3 bg-[#ECEFF1] rounded-[16px] shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
                    <h5 className="text-sm font-medium text-[#002B5C] mb-2">الگوهای هفتگی</h5>
                    <div className="text-xs text-[#4A5568] space-y-1">
                        <div className="flex items-center justify-between">
                            <span>بیشترین جهش قند:</span>
                            <span className="font-medium">چهارشنبه‌ها (ناهار)</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span>بهترین کنترل قند:</span>
                            <span className="font-medium">جمعه‌ها</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span>میانگین جهش روزانه:</span>
                            <span className="font-medium">۲۸ mg/dL</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FoodInsightsGraph;
